import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { MapContainer, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import {
    useClusters,
    useClusterBoundaries,
    useH3Grid,
    useUnclaimedNeighbors,
    useClusterMutations
} from './hooks/useClusters';
import H3GridOverlay from './components/H3GridOverlay';
import UnclaimedHaloOverlay from './components/UnclaimedHaloOverlay';
import ClusterBoundaryLayer from './components/ClusterBoundaryLayer';
import ClusterMapControls from './components/ClusterMapControls';
import ShopMarkersLayer from './components/ShopMarkersLayer';
import { useCoffeeShopsMap } from '../coffee_shops/hooks/useCoffeeShops';
import ClusterList from './components/ClusterList';
import ClusterDetailPanel from './components/ClusterDetailPanel';
import CellMergePanel from './components/CellMergePanel';
import SplitPreview from './components/SplitPreview';
import ShopReviewQueue from './components/ShopReviewQueue';
import ClusterShopsTab from './components/ClusterShopsTab';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMap, faList, faClipboardList, faStore } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { CoffeeMapBasemap, COFFEE_MAP_CONTAINER_CLASS } from '../coffee_shops/components/CoffeeMapBasemap';

const DEFAULT_CENTER: [number, number] = [40.6401, 22.9444];
const DEFAULT_ZOOM = 10;

const MapCenter: React.FC<{ center: [number, number] }> = ({ center }) => {
    const map = useMap();
    React.useEffect(() => {
        map.setView(center, map.getZoom());
    }, [center, map]);
    return null;
};

const ClusterAdminPage: React.FC = () => {
    const [mapCenter, setMapCenter] = useState<[number, number]>(DEFAULT_CENTER);
    const [mapZoom, setMapZoom] = useState(DEFAULT_ZOOM);
    const [selectedClusterId, setSelectedClusterId] = useState<string | null>(null);
    const [mode, setMode] = useState<'view' | 'select'>('view');
    const [selectedCells, setSelectedCells] = useState<Set<string>>(new Set());
    const [splitClusterId, setSplitClusterId] = useState<string | null>(null);
    const [splitSuggestion, setSplitSuggestion] = useState<{
        clusterA: { h3Cells: string[]; shopCount: number };
        clusterB: { h3Cells: string[]; shopCount: number };
    } | null>(null);
    const [fetchingId, setFetchingId] = useState<string | null>(null);
    const [splittingId, setSplittingId] = useState<string | null>(null);
    const [showShops, setShowShops] = useState(false);
    const [mergingId, setMergingId] = useState<string | null>(null);
    const [mergeTargetClusterId, setMergeTargetClusterId] = useState<string | null>(null);

    const { clusters, loading, error, refetch } = useClusters();
    const { coffeeShops: mapShops } = useCoffeeShopsMap();
    const { boundaries } = useClusterBoundaries();
    const { grid, loading: gridLoading, refetch: refetchGrid } = useH3Grid(mapCenter[0], mapCenter[1], 8000);
    const { grid: unclaimedHalo, refetch: refetchUnclaimed } = useUnclaimedNeighbors(selectedClusterId);
    const mutations = useClusterMutations(refetch);

    const selectedCluster = clusters.find(c => c._id === selectedClusterId) || null;

    // merge viewport grid with unclaimed halo so neighboring cells around the selected cluster are clickable
    const clickableGrid = useMemo(() => {
        const byCell = new Map<string, { cell: string; center: { lat: number; lng: number }; clusterId: string | null; clusterName: string | null }>();
        grid.forEach((g) => byCell.set(g.cell, g));
        if (mode === 'select' && selectedClusterId && unclaimedHalo.length > 0) {
            unclaimedHalo.forEach((h) => {
                if (!byCell.has(h.cell)) byCell.set(h.cell, h);
            });
        }
        return Array.from(byCell.values());
    }, [grid, unclaimedHalo, mode, selectedClusterId]);

    useEffect(() => {
        const center = selectedCluster?.center;
        if (!center) return;
        setMapCenter([center.lat, center.lng]);
        setMapZoom(12);
    }, [selectedClusterId, selectedCluster]);

    const handleCellClick = useCallback((cell: string, clusterId: string | null) => {
        setSelectedCells(prev => {
            const next = new Set(prev);
            if (next.has(cell)) next.delete(cell);
            else next.add(cell);
            return next;
        });
    }, []);

    const handleFetch = useCallback(async (id: string) => {
        setFetchingId(id);
        try {
            const data = await mutations.fetchShops(id);
            toast.success(`Found ${data?.found ?? 0}, added ${data?.added ?? 0} new shops`);
            refetchGrid();
            refetchUnclaimed();
        } catch {
            toast.error('Fetch failed');
        } finally {
            setFetchingId(null);
        }
    }, [mutations, refetchGrid, refetchUnclaimed]);

    const handleRefresh = useCallback(async (id: string) => {
        setFetchingId(id);
        try {
            await mutations.refresh(id);
            toast.success('Cluster refreshed');
            refetchGrid();
            refetchUnclaimed();
        } catch {
            toast.error('Refresh failed');
        } finally {
            setFetchingId(null);
        }
    }, [mutations, refetchGrid, refetchUnclaimed]);

    const handleSuggestSplit = useCallback(async (id: string) => {
        try {
            const data = await mutations.suggestSplit(id);
            setSplitClusterId(id);
            setSplitSuggestion(data?.suggestion || null);
        } catch {
            toast.error('Failed to suggest split');
        }
    }, [mutations]);

    const handleConfirmSplit = useCallback(async (
        clusterA: { name: string; h3Cells: string[] },
        clusterB: { name: string; h3Cells: string[] }
    ) => {
        if (!splitClusterId) return;
        setSplittingId(splitClusterId);
        try {
            await mutations.split(splitClusterId, { clusterA, clusterB });
            toast.success('Cluster split');
            setSplitClusterId(null);
            setSplitSuggestion(null);
            refetchGrid();
            refetchUnclaimed();
        } catch {
            toast.error('Split failed');
        } finally {
            setSplittingId(null);
        }
    }, [splitClusterId, mutations, refetchGrid, refetchUnclaimed]);

    const handleMergeCells = useCallback(async (targetClusterId: string) => {
        const target = clusters.find((c) => c._id === targetClusterId);
        if (!target) return;
        setMergingId(targetClusterId);
        try {
            const mergedCells = [...new Set([...target.h3Cells, ...selectedCells])];
            await mutations.update(targetClusterId, { h3Cells: mergedCells });
            toast.success('Cells merged into cluster');
            setSelectedCells(new Set());
            refetchGrid();
            refetchUnclaimed();
        } catch (err: any) {
            const status = err?.response?.status;
            const msg = err?.response?.data?.error;
            if (status === 409) {
                toast.error(msg || 'Some cells are already in another cluster');
            } else {
                toast.error('Merge failed');
            }
        } finally {
            setMergingId(null);
        }
    }, [clusters, selectedCells, mutations, refetchGrid, refetchUnclaimed]);

    const handleCreateClusterFromCells = useCallback(async (
        body: { name: string; h3Cells: string[]; city: string; country: string }
    ) => {
        try {
            await mutations.create(body);
            toast.success('Cluster created');
            setSelectedCells(new Set());
            refetchGrid();
            refetchUnclaimed();
        } catch {
            toast.error('Create failed');
        }
    }, [mutations, refetchGrid, refetchUnclaimed]);

    const handleClearSelection = useCallback(() => {
        setSelectedCells(new Set());
        setMergeTargetClusterId(null);
    }, []);

    return (
        <>
            <style>{`
@media (max-width: 700px) {
    .cadm-page { padding: 12px 8px !important; }
    .cadm-row { flex-wrap: wrap !important; gap: 8px !important; height: auto !important; }
    .cadm-input-full { width: 100% !important; max-width: 100% !important; box-sizing: border-box !important; }
    .cadm-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; margin: 0 -8px; }
    .cadm-grid-1 { grid-template-columns: 1fr !important; }
    .cadm-page h1 { font-size: 1.35rem !important; }
}
            `}</style>
            <div className="container mx-auto p-4 max-w-7xl cadm-page">
            <h1 className="text-2xl font-bold mb-4">Cluster Admin</h1>

            <Tabs defaultValue="map">
                <TabsList className="cadm-row">
                    <TabsTrigger value="map">
                        <FontAwesomeIcon icon={faMap} className="mr-2" />
                        Map
                    </TabsTrigger>
                    <TabsTrigger value="clusters">
                        <FontAwesomeIcon icon={faList} className="mr-2" />
                        Clusters
                    </TabsTrigger>
                    <TabsTrigger value="shops">
                        <FontAwesomeIcon icon={faStore} className="mr-2" />
                        Cluster Shops
                    </TabsTrigger>
                    <TabsTrigger value="review">
                        <FontAwesomeIcon icon={faClipboardList} className="mr-2" />
                        Shop Review
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="map" className="mt-4">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 cadm-grid-1">
                        <div className="lg:col-span-2 space-y-2">
                            <ClusterMapControls
                                mode={mode}
                                onModeChange={setMode}
                                onRefreshGrid={refetchGrid}
                                gridLoading={gridLoading}
                                showShops={showShops}
                                onToggleShops={() => setShowShops((v) => !v)}
                            />
                            <div className="rounded-xl border border-ink/15 overflow-hidden" style={{ height: '500px' }}>
                                <MapContainer
                                    center={mapCenter}
                                    zoom={mapZoom}
                                    className={COFFEE_MAP_CONTAINER_CLASS}
                                    style={{ height: '100%', width: '100%' }}
                                    scrollWheelZoom
                                >
                                    <CoffeeMapBasemap />
                                    <MapCenter center={mapCenter} />
                                    <ClusterBoundaryLayer
                                        clusters={boundaries}
                                        selectedId={selectedClusterId}
                                        onSelect={(id) => setSelectedClusterId(id)}
                                    />
                                    <UnclaimedHaloOverlay grid={unclaimedHalo} />
                                    {showShops && <ShopMarkersLayer shops={mapShops} />}
                                    <H3GridOverlay
                                        grid={clickableGrid}
                                        selectedCells={selectedCells}
                                        onCellClick={handleCellClick}
                                        mode={mode}
                                        targetClusterId={mergeTargetClusterId}
                                    />
                                </MapContainer>
                            </div>
                        </div>
                        <div className="space-y-4">
                            <ClusterList
                                clusters={clusters}
                                loading={loading}
                                error={error}
                                onRetry={refetch}
                                selectedId={selectedClusterId}
                                onSelect={(c) => setSelectedClusterId(c._id)}
                                onFetch={handleFetch}
                                onRefresh={handleRefresh}
                                onSuggestSplit={handleSuggestSplit}
                                fetchingId={fetchingId}
                            />
                            <ClusterDetailPanel
                                cluster={selectedCluster}
                                onFetch={handleFetch}
                                onRefresh={handleRefresh}
                                onSuggestSplit={handleSuggestSplit}
                                isFetching={!!fetchingId}
                            />
                            {mode === 'select' && selectedCells.size > 0 && (
                                <CellMergePanel
                                    selectedCells={selectedCells}
                                    onClearSelection={handleClearSelection}
                                    clusters={clusters}
                                    selectedClusterId={selectedClusterId}
                                    grid={grid}
                                    onMerge={handleMergeCells}
                                    onCreate={handleCreateClusterFromCells}
                                    isMerging={!!mergingId}
                                    onTargetClusterChange={setMergeTargetClusterId}
                                />
                            )}
                            {splitSuggestion && splitClusterId && (
                                <SplitPreview
                                    clusterName={selectedCluster?.name || 'Cluster'}
                                    suggestion={splitSuggestion}
                                    onConfirm={handleConfirmSplit}
                                    onCancel={() => { setSplitClusterId(null); setSplitSuggestion(null); }}
                                    isSplitting={!!splittingId}
                                />
                            )}
                        </div>
                    </div>
                </TabsContent>

                <TabsContent value="clusters" className="mt-4">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 cadm-grid-1">
                        <ClusterList
                            clusters={clusters}
                            loading={loading}
                            error={error}
                            onRetry={refetch}
                            selectedId={selectedClusterId}
                            onSelect={(c) => setSelectedClusterId(c._id)}
                            onFetch={handleFetch}
                            onRefresh={handleRefresh}
                            onSuggestSplit={handleSuggestSplit}
                            fetchingId={fetchingId}
                        />
                        <ClusterDetailPanel
                            cluster={selectedCluster}
                            onFetch={handleFetch}
                            onRefresh={handleRefresh}
                            onSuggestSplit={handleSuggestSplit}
                            isFetching={!!fetchingId}
                        />
                    </div>
                </TabsContent>

                <TabsContent value="shops" className="mt-4">
                    <ClusterShopsTab
                        selectedCluster={selectedCluster}
                        clusters={clusters}
                        onSelectCluster={(c) => setSelectedClusterId(c._id)}
                        onClearCluster={() => setSelectedClusterId(null)}
                        selectedClusterId={selectedClusterId}
                        onFetch={handleFetch}
                        onRefresh={handleRefresh}
                        onFetchEstimate={mutations.fetchEstimate}
                        onSearchPlaces={mutations.searchPlaces as (id: string, q: string) => Promise<{ results: import('./components/ClusterShopSearch').SearchResult[] }>}
                        onAddPlaces={mutations.addPlaces}
                        fetchingId={fetchingId}
                    />
                </TabsContent>

                <TabsContent value="review" className="mt-4">
                    <ShopReviewQueue />
                </TabsContent>
            </Tabs>
            </div>
        </>
    );
};

export default ClusterAdminPage;
